/* ============================================================================
   UMAI — SET BUILDER  ("build your set": pick shirts, the rungs get cheaper)

   Owner: BUNDLE agent. Pairs with snippets/b3g1-bundle.liquid and
   assets/umai-b3g1-bundle.css. Namespace: ub3- / data-ub3-* / UMAI_B3G1.
   Load assets/umai-b3g1.css (DESIGN's system) BEFORE the bundle CSS.

   ---------------------------------------------------------------------------
   THE OFFER  (changed 2026-08-20 — read this before touching the maths)
   ---------------------------------------------------------------------------
   Every eligible t-shirt is $35 UNIT, and the total is a LOOKUP ON THE COUNT:

       1 -> $35.00     2 -> $60.00     3 -> $75.00  (free shipping unlocks)
       4 -> $100.00 (BEST VALUE)       n>=5 -> $100 + (n-4) x $25

   Oversized tees are OUT of the offer entirely and are filtered as an
   ELIGIBILITY rule (title contains "Oversized"), not priced differently.

   This REPLACED a Buy-3-Get-1 BXGY whose total was sum(chosen) - min(chosen).
   None of that survives: there is no cheapest-line logic anywhere in this file,
   and per-shirt price variance no longer enters the arithmetic at all.
   priceLadder() is the single place a total is computed, it is pure, and it is
   unit-tested in node against the offer table (scratchpad/ub3-price-test.js).

   ---------------------------------------------------------------------------
   THE GUARD THAT KEEPS THIS HONEST
   ---------------------------------------------------------------------------
   The catalogue is NOT repriced yet — products.json today still returns 34.99,
   36.99, 44.99 — and checkout still runs the old discount. So the ladder is a
   DISPLAY price that reality does not yet match. Every render therefore also
   computes listTotalCents, the sum of the shirts' ACTUAL catalogue prices, and
   when they disagree the UI names that figure on screen as what checkout will
   charge today. Quoting below what checkout charges is the one unacceptable
   direction, and it is what got the previous pack ladder switched off on
   2026-08-07 (page said $89.97, checkout took $104.97). Do not make this
   notice quieter without also making the ladder true.

   ---------------------------------------------------------------------------
   PRICE UNITS — the 100x bug waiting to happen
   ---------------------------------------------------------------------------
   Liquid `variant.price`          -> INTEGER CENTS      3499
   /products/<handle>.js           -> INTEGER CENTS      3499
   /cart.js line.original_price    -> INTEGER CENTS      3499
   /collections/<h>/products.json  -> DECIMAL STRING     "34.99"   (verified live)

   Everything is normalised to integer cents at exactly one boundary, toCents().
   Anything implausible for a t-shirt is DROPPED from the picker rather than
   displayed — a hidden shirt is a nuisance, a misquoted one is a chargeback.
   ========================================================================== */
(function () {
  'use strict';

  var W = (typeof window !== 'undefined') ? window : {};
  var D = (typeof document !== 'undefined') ? document : null;

  /* ==========================================================================
     1. MONEY
     ========================================================================== */

  /* A t-shirt under $3 is not a t-shirt, it is a units bug. */
  var SANE_MIN_CENTS = 300;
  var SANE_MAX_CENTS = 100000;

  function toCents(raw) {
    if (raw === null || raw === undefined || raw === '') return NaN;
    if (typeof raw === 'number') {
      if (!isFinite(raw)) return NaN;
      /* Integer -> already cents (Liquid, *.js, cart.js).
         Non-integer -> can only be a dollar amount (analytics blobs). */
      return (raw % 1 === 0) ? raw : Math.round(raw * 100);
    }
    var s = String(raw).trim();
    if (!s) return NaN;
    /* products.json always renders two decimals, so a dot means dollars. */
    if (s.indexOf('.') !== -1) {
      var f = parseFloat(s);
      return isFinite(f) ? Math.round(f * 100) : NaN;
    }
    var n = parseInt(s, 10);
    return isFinite(n) ? n : NaN;
  }

  function saneCents(c) {
    return typeof c === 'number' && isFinite(c) && c >= SANE_MIN_CENTS && c <= SANE_MAX_CENTS;
  }

  /* Presentment-aware, lifted from assets/umai-pack.js:44 (shop-currency cents
     through Shopify.currency.rate, same conversion the free-shipping line
     uses). Never build a money string by hand. */
  function money(cents, fmt) {
    if (!isFinite(cents)) return '';
    var cur = (W.Shopify && W.Shopify.currency) || {};
    var rate = parseFloat(cur.rate) || 1;
    var v = cents * rate;
    if (cur.active && cur.active !== 'USD') {
      try {
        return new Intl.NumberFormat(
          (D && D.documentElement && D.documentElement.lang) || 'en',
          { style: 'currency', currency: cur.active }
        ).format(v / 100);
      } catch (e) { /* fall through to the format string */ }
    }
    var f = fmt || (W.theme && W.theme.moneyFormat) || '${{amount}}';
    return String(f)
      .replace(/<[^>]+>/g, '')
      .replace(/\{\{\s*amount[^}]*\}\}/, (v / 100).toFixed(2))
      .trim();
  }

  /* ==========================================================================
     2. PRICING — the flat ladder
     --------------------------------------------------------------------------
     OFFER CHANGED 2026-08-20. Every eligible tee is $35 flat and the total is a
     LOOKUP ON THE COUNT. The previous model (BXGY, total = sum - cheapest) is
     gone, along with every per-shirt price in the arithmetic. Oversized tees are
     out of the offer entirely and are handled as an ELIGIBILITY rule, not a
     pricing one.

         shirts   total    per shirt   saving
              1    3500         3500        0
              2    6000         3000     1000
              3    7500         2500     3000     <- free shipping unlocks
              4   10000         2500     4000     <- BEST VALUE
            n>=5   10000 + (n-4)*2500   2500      <- ASSUMPTION (see below)

     Integer cents throughout. saving = n * flat - total.

     ⚠ THE GUARD THAT STILL MATTERS.
     The catalogue has not been repriced yet — products.json today still returns
     34.99 / 36.99 / 44.99, and checkout is still running the old BXGY. So the
     ladder is a DISPLAY price that reality does not yet agree with. Quoting
     lower than checkout charges is the one unacceptable direction, so this
     function also computes listTotalCents — the sum of the shirts' ACTUAL
     catalogue prices — and flags any mismatch. The UI names that number on
     screen as what checkout will charge today. It is never silently swallowed.

     ⚠ ASSUMPTIONS, flagged rather than hidden:
       - the 5+ rung ($25 each beyond 4) was handed down with the brief and is
         verified by nothing;
       - checkout producing these totals depends on the owner rebuilding the
         store's discounts. This module hardcodes no assumption that it has.
     ========================================================================== */

  /* ── THE TIER TABLE ───────────────────────────────────────────────────────
     Named packs, integer cents. These four are the offer; everything else is
     derived. ULTRA is SEVEN shirts, not eight (owner correction 2026-08-20). */
  var TIERS = [
    { n: 1, cents: 4000,  key: 'single', name: '',              perk: '' },
    { n: 3, cents: 10000, key: 'tier1',  name: 'CORE PACK',  rank: 'TIER I',   perk: 'freeship', gift: '',                blurb: 'Three tees, one price.' },
    { n: 5, cents: 15000, key: 'tier2',  name: 'ELITE PACK', rank: 'TIER II',  perk: 'gift',     gift: '$15 gift card',   blurb: 'The one most people take.' },
    { n: 7, cents: 20000, key: 'ultra',  name: 'ULTRA PACK', rank: 'TIER III', perk: 'ultra',    gift: '$25 gift card',   blurb: 'Every reward, lowest price per tee.' }
  ];
  var UNIT_CENTS = 4000;      /* one shirt, no pack */
  /* Free shipping is a DOLLAR gate ($60, owner 2026-08-20), expressed here in
     shirts because the whole builder counts in shirts. At $40 a shirt two
     shirts is $80 and clears it; one is $40 and does not. If the unit price
     ever moves, this is derived, not typed: ceil(6000 / UNIT_CENTS). */
  var FREE_SHIP_CENTS = 6000;
  var FREE_SHIP_AT = Math.ceil(FREE_SHIP_CENTS / UNIT_CENTS);
  var ULTRA_AT = 7;
  var LADDER_MAX = 40;        /* how far the table is precomputed */

  /* raw(n): cheapest exact combination of packs summing to n shirts.
     Unbounded coin-change; the pack set is tiny so this is trivial.

     price(n) = min(raw(n), min price(m) for every m > n) — a MONOTONIC CAP.
     With the current tiers the raw table is already strictly increasing, so the
     cap changes nothing. It stays because it is the only thing standing between
     a future tier edit and a page that charges MORE for FEWER shirts: with the
     previous eight-shirt ULTRA, raw(7) was $230 against raw(8) = $200, and
     without this pass the page would have quoted the higher number for the
     smaller set. Do not remove it when the numbers happen to look fine. */
  var _ladder = null;
  function ladderTable() {
    if (_ladder) return _ladder;
    var raw = [0], n, k, best, t;
    for (n = 1; n <= LADDER_MAX; n++) {
      best = Infinity;
      for (k = 0; k < TIERS.length; k++) {
        t = TIERS[k];
        if (t.n <= n) {                       /* use this pack, then the rest */
          if (raw[n - t.n] + t.cents < best) best = raw[n - t.n] + t.cents;
        } else if (t.cents < best) {          /* over-buy a bigger pack outright */
          best = t.cents;
        }
      }
      raw[n] = best;
    }
    var price = raw.slice();
    for (n = LADDER_MAX - 1; n >= 1; n--) {   /* suffix minimum = the cap */
      if (price[n + 1] < price[n]) price[n] = price[n + 1];
    }
    _ladder = { raw: raw, price: price };
    return _ladder;
  }

  function ladderTotal(n) {
    if (!(n > 0)) return 0;
    var L = ladderTable();
    if (n <= LADDER_MAX) return L.price[n];
    /* past the precomputed range, repeat the best pack rate */
    var top = TIERS[TIERS.length - 1];
    var packs = Math.floor(n / top.n), rest = n % top.n;
    return packs * top.cents + (rest ? ladderTotal(rest) : 0);
  }

  /* THE HEADLINE MECHANIC. What the NEXT shirt costs is the most persuasive
     true number on this page: a $40 shirt for $10. It is just the step between
     two rungs, so it cannot drift from the totals. */
  function nextShirtCost(n) {
    return ladderTotal(n + 1) - ladderTotal(n);
  }

  function tierAt(n) {
    var found = null;
    for (var i = 0; i < TIERS.length; i++) if (n >= TIERS[i].n) found = TIERS[i];
    return found;
  }
  function nextTierAbove(n) {
    for (var i = 0; i < TIERS.length; i++) if (TIERS[i].n > n) return TIERS[i];
    return null;
  }

  /**
   * @param {number[]} chosen  ACTUAL catalogue prices in cents of the chosen
   *                           variants — used ONLY for the not-$40-yet guard,
   *                           never for the ladder total.
   * @param {object} o  o.unitCents, o.maxSlots
   */
  function priceLadder(chosen, o) {
    o = o || {};
    var unit = (typeof o.unitCents === 'number' && o.unitCents > 0) ? o.unitCents : UNIT_CENTS;
    var maxSlots = o.maxSlots || ULTRA_AT;

    var list = [], i;
    for (i = 0; i < (chosen || []).length; i++) {
      var c = chosen[i];
      if (typeof c === 'number' && isFinite(c) && c > 0) list.push(Math.round(c));
    }
    var n = list.length;

    var total = ladderTotal(n);
    var save = Math.max(0, (n * unit) - total);

    /* Per-shirt is FLOORED, which reproduces the owner's published table
       exactly ($33.33 at three, $28.57 at seven). It understates by up to a
       cent, so the TOTAL is always displayed beside it and is the binding
       number; the per-shirt figure is explicitly a derived "each" display. */
    var each = n > 0 ? Math.floor(total / n) : 0;

    /* reality check for the guard */
    var listTotal = 0, offCount = 0, offIdx = [];
    for (i = 0; i < n; i++) {
      listTotal += list[i];
      if (list[i] !== unit) { offCount++; offIdx.push(i); }
    }

    var atTier = tierAt(n);
    var upTier = nextTierAbove(n);
    var nextCost = (n < maxSlots) ? nextShirtCost(n) : null;

    return {
      filled: n,
      unitCents: unit,
      totalCents: total,
      eachCents: each,
      saveCents: save,

      /* where you are on the ladder */
      tier: atTier ? atTier.key : null,
      tierName: atTier ? atTier.name : '',
      tierN: atTier ? atTier.n : 0,
      atNamedTier: !!(atTier && atTier.n === n),
      freeShip: n >= FREE_SHIP_AT,
      shirtsToFreeShip: Math.max(0, FREE_SHIP_AT - n),
      ultra: n >= ULTRA_AT,
      shirtsToUltra: Math.max(0, ULTRA_AT - n),

      /* THE MECHANIC: what one more shirt costs, and what it unlocks */
      nextShirtCents: nextCost,
      nextIsFree: nextCost === 0,
      nextIsDiscounted: nextCost !== null && nextCost < unit,
      nextUnlocks: (n + 1 === FREE_SHIP_AT) ? 'freeship'
                 : (n + 1 === ULTRA_AT) ? 'ultra' : '',
      next: (nextCost === null) ? null : {
        n: n + 1, totalCents: ladderTotal(n + 1),
        addCents: nextCost, eachCents: Math.floor(ladderTotal(n + 1) / (n + 1))
      },
      /* the next NAMED pack, for when the immediate step is full price */
      nextTier: upTier ? {
        n: upTier.n, name: upTier.name, totalCents: upTier.cents,
        eachCents: Math.floor(upTier.cents / upTier.n),
        addShirts: upTier.n - n,
        addCents: upTier.cents - total
      } : null,

      /* the catalogue-is-not-repriced-yet guard */
      listTotalCents: listTotal,
      offCount: offCount,
      offIndexes: offIdx,
      flatMismatch: offCount > 0,
      worstCaseCents: Math.max(total, listTotal)
    };
  }

  var priceBundle = priceLadder;   /* old name kept so nothing dangles */

  /* Exported before anything else so the node harness can reach it with no DOM
     and so a broken picker can never take the pricing test down with it. */
  W.UMAI_B3G1_PRICE = priceLadder;
  W.UMAI_B3G1_LADDER = ladderTotal;
  W.UMAI_B3G1_NEXTCOST = nextShirtCost;
  W.UMAI_B3G1_TIERS = TIERS;
  W.UMAI_B3G1_RAW = function () { return ladderTable().raw.slice(); };
  W.UMAI_B3G1_TOCENTS = toCents;

  /* ==========================================================================
     3. COPY  (English; every string is logged in scratchpad/I18N-TODO.md)
     ========================================================================== */
  var S = {
    yourSize:      'Your size',
    slotThisOne:   'This one',
    slotEmpty:     'Add a shirt',
    slotRemove:    'Remove',
    slotNext:      'Next up',
    /* THE MECHANIC. A $40 shirt for $10 is the most persuasive true number on
       this page, so it gets the loudest string. The flat $40 steps are stated
       just as plainly — the contrast is what makes the cheap steps land. */
    nextCheap:     'Your next shirt: only [amount]',
    nextFull:      'Your next shirt: [amount]',
    nextFree:      'Your next shirt is FREE',
    /* ── "REACHED", NEVER "UNLOCKED" ─────────────────────────────────────
       House rule, and it is not a style preference. A tier the shopper has
       not got to yet is not locked away from them: every rung is available to
       anyone willing to put that many shirts in the set. "Unlocked" borrows
       the vocabulary of a paywall and describes the offer wrongly. Eight
       strings here said it, and now that the cards render in the buy column
       (see renderSum) they are read on the first screen rather than buried
       1,400px down the page, so the wording is load-bearing.
       Em dashes are out for the same reason they are out everywhere in
       customer-facing UMAI copy. */
    unlockShip:    'and shipping goes free',
    unlockUltra:   'and every ULTRA reward comes with it',
    nextTierHint:  '[n] more → [amount] each',
    tierNow:       '[n] shirts · [amount] each',
    save:          'You save [amount]',
    total:         'Your set',
    rungOn:        'In your set',
    rungDone:      'Reached',
    rungOff:       'Add [n] more',
    ultraOn:       'ULTRA PACK reached',
    ultraPerks:    'Included with ULTRA',
    perkSoon:      'coming soon',
    shipLocked:    '[n] more and shipping is free',
    shipLocked1:   '1 more and shipping is free',
    shipOpen:      'Free shipping reached',
    ctaAdd:        'Add [n] to Bag, [amount]',
    ctaAdd1:       'Add 1 to Bag, [amount]',
    ctaEmpty:      'Pick a shirt to start your set',
    ctaAdding:     'Adding…',
    ctaAdded:      '✓ Added to bag',
    /* the $40-that-isn't-$40-yet guard. Deliberately blunt. */
    mismatchOne:   '1 shirt is not [amount] in the catalogue yet',
    mismatchMany:  '[n] shirts are not [amount] in the catalogue yet',
    mismatchWhat:  'Checkout charges [amount] today',
    cartNote:      'Your bag already has tees. They count toward your set.',
    searchPh:      'Search t-shirts',
    tabTrend:      'Trending',
    tabNew:        'New',
    tabAll:        'All',
    loadMore:      'Load more designs',
    empty:         'Nothing matched that. Try another word.',
    noSize:        'Not in [size]. Pick another size',
    done:          'Done',
    addErr:        'Could not add to bag. Please try again.',
    badgeNew:      'NEW',
    gridTitle:     'Build your set',
    gridSub:       'Tap a shirt to add it. The more you take, the less each one costs.'
  };
  function fill(str, vars) {
    var s = str;
    for (var p in vars) if (Object.prototype.hasOwnProperty.call(vars, p)) {
      s = s.split('[' + p + ']').join(vars[p]);
    }
    return s;
  }

  /* ==========================================================================
     4. TELEMETRY — fan out to whatever the page already has, never depend on it
     ========================================================================== */
  function track(name, data) {
    var ev = 'umai_b3g1_' + name;
    var payload = data || {};
    try {
      W.dataLayer = W.dataLayer || [];
      var row = { event: ev };
      for (var k in payload) row[k] = payload[k];
      W.dataLayer.push(row);
    } catch (e) {}
    try { if (typeof W.fbq === 'function') W.fbq('trackCustom', ev, payload); } catch (e) {}
    try { if (W._learnq) W._learnq.push(['track', ev, payload]); } catch (e) {}
    try { D.dispatchEvent(new CustomEvent(ev, { detail: payload })); } catch (e) {}
  }
  function emit(name, detail) {
    try { D.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {}
  }

  /* ==========================================================================
     5. NORMALISERS — one per source shape, each converting price explicitly.
     Never guess the units from the number; guess them from where it came from.
     ========================================================================== */

  function sizeIndexOf(options) {
    /* options can be [{name,position}] (products.json, *.js) or ['Color','Size']
       (older *.js). Match by NAME — option1 is only "the colour" by luck. */
    if (!options || !options.length) return 0;
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      var n = (typeof o === 'string' ? o : (o && o.name) || '').toLowerCase();
      if (n === 'size') return (typeof o === 'object' && o.position) ? o.position : (i + 1);
    }
    return 0;
  }
  function otherIndexOf(options, sizeIdx) {
    if (!options || !options.length) return 0;
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      var pos = (typeof o === 'object' && o.position) ? o.position : (i + 1);
      if (pos !== sizeIdx) return pos;
    }
    return 0;
  }

  function imgUrl(raw, width) {
    var u = raw || '';
    if (!u) return '';
    if (u.indexOf('//') === 0) u = 'https:' + u;          /* *.js is protocol-relative */
    if (width && u.indexOf('width=') === -1 && u.indexOf('/cdn/') > -1) {
      u += (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }
    return u;
  }

  function finishRow(row) {
    /* Drop variants whose price did not normalise into a plausible range, and
       drop the whole product if none survive. Better a missing shirt than a
       shirt quoted at $0.35. */
    var good = [];
    for (var i = 0; i < row.v.length; i++) {
      var v = row.v[i];
      if (!v || !v.id) continue;
      if (!saneCents(v.p)) continue;
      good.push(v);
    }
    if (!good.length) return null;
    row.v = good;
    row.min = good[0].p;
    row.hasStock = false;
    for (i = 0; i < good.length; i++) {
      if (good[i].p < row.min) row.min = good[i].p;
      if (good[i].a) row.hasStock = true;
    }
    if (!row.hasStock) return null;   /* fully sold-out shirts never get a card */
    if (!row.t) return null;
    return row;
  }

  /* --- Liquid seed (this snippet's config blob). Prices ALREADY CENTS. --- */
  function fromSeed(r) {
    if (!r || !r.i) return null;
    var v = [];
    for (var i = 0; i < (r.v || []).length; i++) {
      var s = r.v[i];
      v.push({ id: s.i, s: String(s.s == null ? '' : s.s), c: s.c || '', a: !!s.a, p: toCents(s.p) });
    }
    return finishRow({ id: r.i, h: r.h || '', t: r.t || '', img: imgUrl(r.g, 400), pub: (r.n || 0) * 1000, v: v });
  }

  /* --- /collections/<h>/products.json. Prices are DECIMAL STRINGS. --- */
  function fromProductsJson(p) {
    if (!p || !p.id) return null;
    var si = sizeIndexOf(p.options), oi = otherIndexOf(p.options, si);
    var img = '';
    if (p.images && p.images[0]) img = p.images[0].src || p.images[0];
    else if (p.featured_image) img = p.featured_image.src || p.featured_image;
    var v = [];
    for (var i = 0; i < (p.variants || []).length; i++) {
      var x = p.variants[i];
      v.push({
        id: x.id,
        s: String((si && x['option' + si]) || x.title || ''),
        c: String((oi && x['option' + oi]) || ''),
        a: x.available !== false,
        p: toCents(x.price)          /* "34.99" -> 3499 */
      });
    }
    return finishRow({
      id: p.id, h: p.handle || '', t: p.title || '',
      img: imgUrl(img, 400),
      pub: p.published_at ? Date.parse(p.published_at) : (p.created_at ? Date.parse(p.created_at) : 0),
      v: v
    });
  }

  /* --- /products/<handle>.js. Prices are INTEGER CENTS. --- */
  function fromProductJs(p) {
    if (!p || !p.id) return null;
    var si = sizeIndexOf(p.options), oi = otherIndexOf(p.options, si);
    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && typeof img === 'object') img = img.src || '';
    var v = [];
    for (var i = 0; i < (p.variants || []).length; i++) {
      var x = p.variants[i];
      v.push({
        id: x.id,
        s: String((si && x['option' + si]) || x.title || ''),
        c: String((oi && x['option' + oi]) || ''),
        a: x.available !== false,
        p: toCents(x.price)          /* 3499 -> 3499 */
      });
    }
    return finishRow({
      id: p.id, h: p.handle || '', t: p.title || '',
      img: imgUrl(img, 400),
      pub: p.published_at ? Date.parse(p.published_at) : 0,
      v: v
    });
  }

  /* --- the slim Liquid feed, if COMPOSITION ships one (see HANDOFF-BUNDLE.md).
     Same short keys as the seed, so one normaliser serves both. --- */
  var fromSlim = fromSeed;

  /* Exposed for the offline harness in scratchpad/ub3-normalise-test.js, which
     runs these over REAL captured payloads from all three endpoints — the three
     disagree about price units, and this is how we prove they land on the same
     integer cents. Read-only; nothing in the module depends on it existing. */
  W.UMAI_B3G1_DEBUG = {
    fromSeed: fromSeed,
    fromProductsJson: fromProductsJson,
    fromProductJs: fromProductJs,
    toCents: toCents,
    saneCents: saneCents,
    sizeIndexOf: sizeIndexOf
  };

  /* ==========================================================================
     6. CATALOGUE STORE
     ========================================================================== */
  function Store(cfg) {
    this.cfg = cfg;
    this.byId = {};
    this.rows = [];
    this.collPage = 0;
    this.collDone = false;
    this.slimDead = false;     /* a 404 on the slim view is permanent... */
    this.failStreak = 0;       /* ...a 429 is not. Never conflate them. */
    this.inflight = null;
    this.hydrating = {};
  }
  Store.prototype.add = function (row, src) {
    if (!row) return false;
    var have = this.byId[row.id];
    if (have) {
      if (src === 'coll' && have._c == null) have._c = this.rows.length;
      return false;
    }
    row._r = this.rows.length;                 /* arrival order == merchandised order */
    if (src === 'coll') row._c = row._r;
    this.byId[row.id] = row;
    this.rows.push(row);
    return true;
  };
  Store.prototype.all = function () { return this.rows; };
  Store.prototype.get = function (id) { return this.byId[id]; };

  function root(cfg) {
    var r = (cfg.routes && cfg.routes.root) || (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) {
          var e = new Error('HTTP ' + r.status);
          e.status = r.status;
          throw e;
        }
        /* ⚠ A `?view=` that does not exist does NOT 404 on Shopify — it renders
           the collection's normal HTML page with a 200. Verified on the dev
           server 2026-08-20: /collections/all-t-shirts?view=b3g1json -> 200 HTML.
           So "is this JSON" is a shape question, never a status question, and a
           parse failure has to be distinguishable from a network failure. */
        return r.text().then(function (tx) {
          try { return JSON.parse(tx); }
          catch (e2) {
            var pe = new Error('not JSON');
            pe.parse = true;
            throw pe;
          }
        });
      });
  }

  /* One page of the tee collection.
     VERIFIED on the dev server, 2026-08-20:
       /collections/all-t-shirts/products.json?limit=50&page=N   -> 200
       397 products over 8 pages (page 8 returns 47). variant.price is the
       DECIMAL STRING "34.99"; `available` is present per variant; `options`
       carry {name, position} and Size is position 2 on this catalogue, which
       is exactly why sizeIndexOf() matches by name and not by position.
       Observed price spread across the collection: 29.99 -> 48.00. A mixed
       basket is the normal case here, not an edge case.
     Page weight is real: limit=250 is 2.6MB, limit=50 is ~514KB. So nothing is
     fetched until the shopper opens the picker and runs past the Liquid seed.

     A slim Liquid feed would be ~20x lighter, but a `view` template that does
     not exist returns 200 HTML rather than 404 (see getJSON), so we never probe
     for one speculatively — that probe would itself download a full collection
     page. It is used only when cfg.feeds.slim names a view that really exists,
     and the FIRST bad shape disables it permanently. */
  Store.prototype.page = function (n) {
    var self = this, cfg = this.cfg, base = root(cfg);
    var handle = (cfg.feeds && cfg.feeds.collection) || 'all-t-shirts';
    var slim = (cfg.feeds && cfg.feeds.slim) || '';

    function viaProductsJson() {
      return getJSON(base + 'collections/' + handle + '/products.json?limit=50&page=' + n)
        .then(function (j) {
          var list = (j && j.products) || [];
          if (!list.length && n === 1) throw new Error('empty catalogue');
          return { rows: list.map(fromProductsJson), end: list.length < 50 };
        });
    }
    if (!slim || this.slimDead) return viaProductsJson();

    return getJSON(base + 'collections/' + handle + '?view=' + slim + '&page=' + n)
      .then(function (j) {
        var list = (j && j.products) || [];
        /* the slim row must carry a handle AND a per-variant price, or it
           cannot be priced and is worse than useless */
        var r0 = list[0];
        var okShape = list.length && r0 && r0.h && r0.v && r0.v[0] && r0.v[0].p != null;
        if (!okShape) { self.slimDead = true; return viaProductsJson(); }
        return { rows: list.map(fromSlim), end: (j && j.pages) ? n >= j.pages : list.length < 20 };
      })
      .catch(function (err) {
        if (err && (err.parse || err.status === 404)) { self.slimDead = true; return viaProductsJson(); }
        throw err;                                  /* 429 etc. stays retryable */
      });
  };

  Store.prototype.more = function () {
    var self = this;
    if (this.collDone) return Promise.resolve(0);
    if (this.inflight) return this.inflight;           /* chain, never race */
    if (this.failStreak >= 3) return Promise.resolve(0);
    var n = this.collPage + 1;
    var depth = (typeof arguments[0] === 'number') ? arguments[0] : 0;
    this.inflight = this.page(n).then(function (res) {
      self.inflight = null;
      self.failStreak = 0;
      self.collPage = n;
      var added = 0;
      for (var i = 0; i < res.rows.length; i++) if (self.add(res.rows[i], 'coll')) added++;
      if (res.end) self.collDone = true;
      /* The Liquid seed is drawn from the same best-selling order, so the first
         network page can be almost entirely duplicates. Chain rather than hand
         back an empty "Load more". */
      if (added < 6 && !self.collDone && depth < 3) return self.more(depth + 1);
      return added;
    }).catch(function () {
      self.inflight = null;
      self.failStreak++;
      return 0;                                        /* retryable: page cursor not advanced */
    });
    return this.inflight;
  };

  /* Long-tail search without downloading the whole catalogue. suggest.json
     gives handles; /products/<h>.js gives prices we can trust the units of. */
  Store.prototype.suggest = function (q) {
    var self = this, base = root(this.cfg);
    var url = base + 'search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
    return getJSON(url).then(function (j) {
      var res = (j && j.resources && j.resources.results && j.resources.results.products) || [];
      var jobs = [];
      for (var i = 0; i < res.length; i++) {
        var p = res[i];
        var h = p && p.handle;
        if (!h) continue;
        if (String(p.title || '').indexOf('T-Shirt') === -1) continue;
        jobs.push(self.hydrate(h));
      }
      return Promise.all(jobs);
    }).then(function (rows) {
      var added = 0;
      for (var i = 0; i < rows.length; i++) if (self.add(rows[i], 'search')) added++;
      return added;
    }).catch(function () { return 0; });
  };

  Store.prototype.hydrate = function (handle) {
    var self = this;
    if (this.hydrating[handle]) return this.hydrating[handle];
    var pr = getJSON(root(this.cfg) + 'products/' + handle + '.js')
      .then(fromProductJs)
      .catch(function () { return null; });
    this.hydrating[handle] = pr;
    return pr;
  };

  /* ==========================================================================
     7. THE MODULE
     ========================================================================== */
  var PAGE_CARDS = 24;
  var SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL'];
  var SIZE_KEY = 'umai:b3g1:size';
  var BUNDLE_KEY = 'umai:b3g1:v1';

  function ls(k, v) {
    try {
      if (v === undefined) return W.localStorage.getItem(k);
      W.localStorage.setItem(k, v);
    } catch (e) {}
    return null;
  }
  function ss(k, v) {
    try {
      if (v === undefined) return W.sessionStorage.getItem(k);
      W.sessionStorage.setItem(k, v);
    } catch (e) {}
    return null;
  }

  function sizeRank(s) {
    var i = SIZE_ORDER.indexOf(String(s).toUpperCase().replace(/\s+/g, ''));
    return i === -1 ? 99 : i;
  }

  /* ELIGIBILITY. As of the 2026-08-20 offer change this is NOT the same rule as
     snippets/umai-offer.liquid's `eligible()`: that one folds Oversized IN (a
     2026-08-19 call for the old BOGO), and the flat-$35 ladder folds it OUT
     because Oversized blanks are ~$44.99. So this module owns its own rule and
     says so, rather than delegating to a rule that now disagrees with it.

     `t` may be a string OR a /cart.js line item. Handling only the string case
     here is how you get String({...}) === "[object Object]" and a cart that
     silently reports zero tees. */
  function titleOf(t) {
    if (typeof t === 'string') return t;
    if (!t) return '';
    return t.product_title || t.title || '';
  }
  function isTeeTitle(t) {
    var name = titleOf(t);
    if (name.indexOf('T-Shirt') === -1) return false;
    if (/oversized/i.test(name)) return false;     /* OUT of the flat ladder */
    return true;
  }

  function init(rootEl) {
    if (rootEl.__ub3) return;
    rootEl.__ub3 = true;

    var cfgEl = rootEl.querySelector('[data-ub3-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    if (!cfg || !cfg.current || !cfg.current.v || !cfg.current.v.length) return;

    /* The offer can be flipped off in snippets/umai-offer.liquid without anyone
       remembering this file exists. If the live offer is not THIS ladder, stay
       hidden and let the page's normal single-shirt purchase be the whole story.

       ⚠ THE ALLOW-LIST IS THE WHOLE GATE, AND IT WENT STALE ONCE ALREADY.
       This was `if (mode !== 'b3g1') return;`. snippets/umai-offer.liquid moved
       MODE to 'packs' on 2026-08-20 when the buy-3-get-1 cycle became the 3/5/7
       pack ladder — the same ladder this file prices — and from that moment the
       builder returned here on every page load. Nothing threw and nothing was
       logged, so the only symptom was the root keeping the `hidden` attribute
       snippets/b3g1-bundle.liquid ships it with: no slots, no tray, and no pack
       cards anywhere on the PDP. The offer was invisible for a day and the page
       read as a plain product page with a large red button that scrolled to an
       empty anchor.

       So the gate is a LIST, not an equality, and a mode this file genuinely
       serves has to be in it. 'b3g1' stays for the A/B control arm and for any
       cached copy of umai-offer.liquid still declaring the old name. */
    var mode = (W.UMAI_OFFER && W.UMAI_OFFER.mode) || 'packs';
    if (mode !== 'packs' && mode !== 'b3g1') return;

    var MAXSLOTS = cfg.maxSlots || ULTRA_AT;
    var UNIT = cfg.unitCents || UNIT_CENTS;
    var PERKS = (cfg.perks && cfg.perks.length) ? cfg.perks : [];
    var STRICT = !!cfg.strictFlat;
    var LAYOUT = cfg.layout === 'column' ? 'column' : 'page';
    var FMT = cfg.moneyFormat || null;
    var M = function (c) { return money(c, FMT); };

    var store = new Store(cfg);

    /* ---- current product, as a store row ---- */
    var cur = fromSeed({
      i: cfg.current.id, h: cfg.current.h, t: cfg.current.t, g: cfg.current.g,
      n: 0, v: cfg.current.v
    });
    if (!cur) return;                    /* prices did not normalise — say nothing */
    store.add(cur, 'coll');
    cur._pin = true;

    for (var si = 0; si < (cfg.seed || []).length; si++) {
      var r = fromSeed(cfg.seed[si]);
      if (r && isTeeTitle(r.t)) store.add(r, 'coll');
    }
    store.collPage = 0;   /* the seed is a subset of page 1; start paging at 1 */

    /* ---- state ---- */
    var slots = new Array(MAXSLOTS);
    for (var i0 = 0; i0 < MAXSLOTS; i0++) slots[i0] = null;
    var prefSize = null;
    var cartTees = [];
    var lastQuote = null;

    function variantOf(row, sizeName) {
      if (!row) return null;
      var want = String(sizeName || '').toUpperCase();
      var best = null, i;
      for (i = 0; i < row.v.length; i++) {
        var v = row.v[i];
        if (String(v.s).toUpperCase() !== want) continue;
        if (v.a) return v;                 /* available in this size wins outright */
        if (!best) best = v;               /* remember it exists but is sold out */
      }
      return best;
    }
    function firstAvailable(row) {
      for (var i = 0; i < (row ? row.v : []).length; i++) if (row.v[i].a) return row.v[i];
      return null;
    }
    function sizesOf(row) {
      var seen = {}, out = [];
      for (var i = 0; i < (row ? row.v : []).length; i++) {
        var v = row.v[i];
        var key = String(v.s);
        if (seen[key]) { if (v.a) seen[key].a = true; continue; }
        seen[key] = { s: key, a: !!v.a, p: v.p, id: v.id };
        out.push(seen[key]);
      }
      out.sort(function (a, b) { return sizeRank(a.s) - sizeRank(b.s) || (a.s < b.s ? -1 : 1); });
      return out;
    }

    function makePick(row, variant) {
      return {
        pid: row.id, h: row.h, title: row.t, img: row.img,
        vid: variant.id, size: variant.s, price: variant.p, avail: variant.a
      };
    }

    /* ---- the shopper's size, decided once ---- */
    function allSizes() {
      /* union across the current product plus the seeded catalogue, so the
         size bar is the store's real size run, not just this shirt's. */
      var seen = {}, out = [], rows = store.all(), i, j;
      for (i = 0; i < rows.length && i < 60; i++) {
        for (j = 0; j < rows[i].v.length; j++) {
          var s = String(rows[i].v[j].s);
          if (!s || seen[s]) continue;
          seen[s] = 1; out.push(s);
        }
      }
      out.sort(function (a, b) { return sizeRank(a) - sizeRank(b) || (a < b ? -1 : 1); });
      return out;
    }

    function setSize(s, quiet) {
      prefSize = s || null;
      if (s) ls(SIZE_KEY, s);
      /* Re-point every slot at the same shirt in the new size where it exists;
         a slot that does not stock it keeps its own size as an override. */
      for (var i = 0; i < MAXSLOTS; i++) {
        var p = slots[i];
        if (!p || p.locked) continue;
        var row = store.get(p.pid);
        var v = variantOf(row, prefSize);
        if (v && v.a) slots[i] = makePick(row, v);
      }
      if (!quiet) { renderAll(); save(); }
    }

    /* ---- persistence: a bundle in progress survives browsing to another tee ---- */
    function save() {
      try {
        var keep = [];
        for (var i = 0; i < MAXSLOTS; i++) {
          var p = slots[i];
          keep.push(p && p.pid !== cur.id ? p : null);
        }
        ss(BUNDLE_KEY, JSON.stringify({ t: Date.now(), s: keep, z: prefSize }));
      } catch (e) {}
    }
    function restore() {
      var raw = ss(BUNDLE_KEY);
      if (!raw) return;
      try {
        var j = JSON.parse(raw);
        if (!j || !j.t || (Date.now() - j.t) > 2 * 60 * 60 * 1000) return;
        if (j.z && !prefSize) prefSize = j.z;
        for (var i = 0; i < MAXSLOTS && i < (j.s || []).length; i++) {
          var p = j.s[i];
          if (p && p.vid && p.pid !== cur.id && saneCents(p.price)) slots[i] = p;
        }
      } catch (e) {}
    }

    /* ---- cart awareness (see priceBundle's cart branches) ---- */
    var cartKnown = false;
    function readCart() {
      return getJSON(root(cfg) + 'cart.js').then(function (c) {
        var tees = [], i;
        for (i = 0; i < ((c && c.items) || []).length; i++) {
          var it = c.items[i];
          if (!isTeeTitle(it)) continue;          /* takes the line item, not a string */
          /* ⚠ /cart.js `price` is an alias of final_price — POST line discount.
             A tee already discounted to 0 reads as 0 and would vanish from the
             count. original_price is the pre-discount figure and is the one
             that answers "how many eligible tees are in this bag". */
          var unit = toCents(it.original_price != null ? it.original_price : it.price);
          for (var q = 0; q < (it.quantity || 1); q++) tees.push(unit);
        }
        cartTees = tees;
        cartKnown = true;
        renderSum();
      }).catch(function () {
        /* A cart we could not read is NOT an empty cart. Say nothing about it
           rather than rendering a note premised on it being empty. */
        cartKnown = false;
        cartTees = [];
      });
    }

    /* ---- quoting ---- */
    function chosenPrices() {
      var out = [];
      for (var i = 0; i < MAXSLOTS; i++) if (slots[i]) out.push(slots[i].price);
      return out;
    }
    function quote() {
      return priceLadder(chosenPrices(), { unitCents: UNIT, maxSlots: MAXSLOTS });
    }

    /* ======================================================================
       RENDER — slots
       ====================================================================== */
    var elSlots   = rootEl.querySelector('[data-ub3-slots]');
    var elSizes   = rootEl.querySelector('[data-ub3-sizelist]');
    var elMeter   = rootEl.querySelector('[data-ub3-meter]');
    var elLadder  = rootEl.querySelector('[data-ub3-ladder]');
    /* ── THE PACK CARDS ALSO RENDER OUTSIDE THIS ROOT ────────────────────
       The builder owns the whole page width and therefore sits BELOW the buy
       column, which is the right place for the slot picker and the wrong place
       for the offer: measured on 2026-08-20 the first pack card began 1,392px
       down a 4,052px page, so on a 1280x800 desktop nobody saw the ladder at
       all without scrolling past the gallery, the delivery facts and three
       accordions.

       Rather than move the builder or draw a second, hand-written copy of the
       ladder in the section (two ladders is how two ladders drift — the defect
       the header comment of snippets/b3g1-bundle.liquid was written about),
       renderSum() paints the SAME generated markup into every mount on the
       page. sections/product-b3g1.liquid puts one in the buy column. The
       document-level query is deliberate: a mount is by definition outside
       rootEl. Live state (off / done / on) reaches every copy because there is
       one render, not one per mount. */
    var ladderMounts = D.querySelectorAll('[data-ub3-ladder-mount]');
    var elNext    = rootEl.querySelector('[data-ub3-next]');
    var elPerks   = rootEl.querySelector('[data-ub3-perks]');
    var elMeterF  = rootEl.querySelector('[data-ub3-meterfill]');
    var elLedger  = rootEl.querySelector('[data-ub3-ledger]');
    var elShip    = rootEl.querySelector('[data-ub3-ship]');
    var elShipFill= rootEl.querySelector('[data-ub3-shipfill]');
    var elShipTxt = rootEl.querySelector('[data-ub3-shiptxt]');
    var elCta     = rootEl.querySelector('[data-ub3-cta]');
    var elCtaTxt  = rootEl.querySelector('[data-ub3-ctatxt]');
    var elErr     = rootEl.querySelector('[data-ub3-err]');
    var elNote    = rootEl.querySelector('[data-ub3-note]');

    /* Every number on screen goes inside .ub3-num — DESIGN's mono + tabular
       figures. That is not only the look: without tabular numerals the bundle
       total jitters horizontally every time it recalculates. */
    function num(txt) { return '<span class="ub3-num">' + esc(txt) + '</span>'; }

    function renderSizes() {
      if (!elSizes) return;
      var list = allSizes(), html = '', i;
      for (i = 0; i < list.length; i++) {
        var sName = list[i];
        var on = (prefSize && String(prefSize).toUpperCase() === String(sName).toUpperCase());
        html += '<button type="button" class="ub3-chip ub3-chip--size" data-ub3-setsize="' + esc(sName) +
          '" aria-pressed="' + (on ? 'true' : 'false') + '">' + esc(sName) + '</button>';
      }
      elSizes.innerHTML = html;
      if (elSheetSizes) {
        elSheetSizes.innerHTML =
          '<span class="ub3-eyebrow ub3-b3g1-szrail__lbl">' + esc(S.yourSize) + '</span>' + html;
      }
    }

    /* Map priceBundle's index into the compacted chosen[] array back onto the
       slot grid, which can be filled out of order (slots 0 and 2 filled, 1 and
       3 empty is a perfectly ordinary state). */

    /* How many slot tiles to draw: always at least the BEST VALUE target, then
       one spare ahead of the shopper so there is visibly somewhere to go. */
    function slotCount(q) {
      return Math.max(4, Math.min(MAXSLOTS, (q ? q.filled : 0) + 1));
    }

    function slotHTML(i, q) {
      var p = slots[i];
      var n = '<span class="ub3-slot__n">' + (i + 1) + '</span>';

      if (!p) {
        /* the next empty tile is the acid one: "put a shirt here" */
        var isNext = (i === nextGap());
        return '<li class="ub3-slot" data-state="' + (isNext ? 'free' : 'empty') + '" data-ub3-slot="' + i + '">' +
          n +
          '<button type="button" class="ub3-b3g1-hit" data-ub3-open="' + i + '">' +
            '<span class="ub3-b3g1-plus" aria-hidden="true">+</span>' +
            '<span class="ub3-sr">' + esc(S.slotEmpty) + ' ' + (i + 1) + '</span>' +
          '</button>' +
          (isNext ? '<span class="ub3-slot__tag">' + esc(S.slotNext) + '</span>' : '') +
          '</li>';
      }

      /* once the set hits BEST VALUE every tile locks in — the "you did it"
         pixel. Below that they are simply filled. */
      var state = (q && q.ultra) ? 'locked' : 'filled';
      var off = (p.price !== UNIT);
      return '<li class="ub3-slot" data-state="' + state + '"' +
        (off ? ' data-ub3-off="true"' : '') + ' data-ub3-slot="' + i + '">' +
        n +
        (p.img ? '<img class="ub3-slot__img" src="' + esc(p.img) + '" alt="" loading="lazy" decoding="async">' : '') +
        '<button type="button" class="ub3-b3g1-hit" data-ub3-open="' + i + '">' +
          '<span class="ub3-sr">' + esc(shortTitle(p.title)) + ' — change</span>' +
        '</button>' +
        '<button type="button" class="ub3-slot__x" data-ub3-clear="' + i + '" aria-label="' +
          esc(S.slotRemove) + ' ' + esc(shortTitle(p.title)) + '">&times;</button>' +
        '<button type="button" class="ub3-slot__tag ub3-b3g1-tag" data-ub3-slotsize="' + i + '">' +
          esc(p.size || '?') +
        '</button>' +
        '</li>';
    }

    function renderSlots(q) {
      if (!elSlots) return;
      q = q || lastQuote || quote();
      var html = '', cnt = slotCount(q);
      for (var i = 0; i < cnt; i++) html += slotHTML(i, q);
      elSlots.innerHTML = html;
      elSlots.setAttribute('data-count', cnt);
    }

    /* ======================================================================
       RENDER — the money.
       The ledger shows the arithmetic rather than asserting a number: four
       line items, the cheapest struck through, then the total. A shopper
       holding a $44.99 oversized tee and three $34.99 tees can see for
       themselves that the $34.99 is the one going free — which is exactly the
       thing that surprises people if it is only implied.
       Every figure here comes out of priceLadder(). No arithmetic in this
       function beyond a percentage for the meter width.
       ====================================================================== */
    function renderSum() {
      var q = quote();
      lastQuote = q;
      var i;

      /* Let COMPOSITION react to the tier without duplicating the ladder. */
      rootEl.setAttribute('data-ub3-tier', q.tier || 'none');
      rootEl.setAttribute('data-ub3-count', q.filled);
      rootEl.setAttribute('data-ub3-ultra', q.ultra ? 'true' : 'false');

      /* ── the rungs: the four named packs, escalating ─────────────────── */
      if (elLadder || ladderMounts.length) {
        /* THE PACK CARDS.
           These used to be four numeric rungs in a single row: n, unit, total.
           That reads as a price table, and a price table is something you check
           rather than something you pick from. Owner's direction 2026-08-20 was
           game tiers, so each named pack is now a CARD that states what it costs,
           what each tee works out at, and what comes with it. The unnamed n=1
           entry is deliberately NOT drawn: it is the baseline the cards are
           measured against, and it already has a home in the "next shirt" line.

           State drives the whole look and is the same three values as before, so
           nothing downstream had to change: off (not reached), done (reached),
           on (the pack currently pricing the set). */
        var rungs = '', t, k, tierState, save;
        for (k = 0; k < TIERS.length; k++) {
          t = TIERS[k];
          if (!t.name) continue;
          tierState = (q.filled >= t.n) ? 'done' : 'off';
          if (q.tierN === t.n && q.filled >= t.n) tierState = 'on';
          save = (t.n * UNIT_CENTS) - t.cents;
          rungs += '<li class="ub3-b3g1-rung" data-state="' + tierState + '" data-tier="' + t.key + '">' +
            (t.key === 'ultra' ? '<span class="ub3-b3g1-rung__sheen" aria-hidden="true"></span>' : '') +
            '<span class="ub3-b3g1-rung__top">' +
              '<span class="ub3-b3g1-rung__rank">' + esc(t.rank) + '</span>' +
              '<span class="ub3-b3g1-rung__name">' + esc(t.name) + '</span>' +
              (save > 0 ? '<span class="ub3-b3g1-rung__save ub3-num">SAVE ' + esc(M(save)) + '</span>' : '') +
            '</span>' +
            '<span class="ub3-b3g1-rung__line">' +
              '<span class="ub3-num ub3-b3g1-rung__qty">' + t.n + ' for ' + esc(M(t.cents)) + '</span>' +
              '<span class="ub3-num ub3-b3g1-rung__p">' + esc(M(Math.floor(t.cents / t.n))) + ' each</span>' +
            '</span>' +
            (t.blurb ? '<span class="ub3-b3g1-rung__blurb">' + esc(t.blurb) + '</span>' : '') +
            '<span class="ub3-b3g1-rung__perks">' +
              (t.n >= FREE_SHIP_AT ? '<span class="ub3-b3g1-rung__perk">Free shipping</span>' : '') +
              (t.gift ? '<span class="ub3-b3g1-rung__perk is-hero">' + esc(t.gift) + '</span>' : '') +
              (t.perk === 'ultra' ? '<span class="ub3-b3g1-rung__perk is-hero">4K wallpaper pack</span>' +
                                    '<span class="ub3-b3g1-rung__perk is-hero">One month of UMAI+</span>' : '') +
            '</span>' +
            '<span class="ub3-b3g1-rung__foot">' +
              (tierState === 'on' ? esc(S.rungOn || 'In your set')
                                  : tierState === 'done' ? esc(S.rungDone || 'Reached')
                                  : esc((S.rungOff || 'Add [n]').replace('[n]', String(t.n - q.filled)))) +
            '</span>' +
            '</li>';
        }
        /* One build, N destinations. Written with innerHTML per mount rather
           than cloning a node, because a clone would need the same escaping
           story and this string is already escaped at source. */
        var ladderHTML = '<ol class="ub3-b3g1-rungs ub3-b3g1-rungs--cards">' + rungs + '</ol>';
        if (elLadder) elLadder.innerHTML = ladderHTML;
        for (var lm = 0; lm < ladderMounts.length; lm++) {
          ladderMounts[lm].innerHTML = ladderHTML;
          /* Tell the host block it has something to show. The section ships
             its pack block display:none and reveals it on this attribute, so
             a builder that never boots leaves no orphaned heading behind. */
          var lmHost = ladderMounts[lm].parentNode;
          if (lmHost && lmHost.setAttribute) lmHost.setAttribute('data-ub3-packs', 'ready');
        }
      }

      /* ── THE MECHANIC: what the next shirt costs ──────────────────────────
         Loud when it is cheap, plain when it is not. Hiding the $40 steps would
         make the $10 ones look like a trick; showing both is what sells them. */
      if (elNext) {
        if (q.nextShirtCents === null || q.filled >= MAXSLOTS) {
          elNext.hidden = true;
          elNext.innerHTML = '';
        } else {
          elNext.hidden = false;
          var cheap = q.nextIsDiscounted, free = q.nextIsFree;
          var line = free ? S.nextFree
                   : fill(cheap ? S.nextCheap : S.nextFull, { amount: M(q.nextShirtCents) });
          var unlock = q.nextUnlocks === 'freeship' ? S.unlockShip
                     : q.nextUnlocks === 'ultra' ? S.unlockUltra : '';
          var hint = '';
          /* full-price step: point at the rung that is actually worth reaching */
          if (!cheap && q.nextTier) {
            hint = '<span class="ub3-meta ub3-muted ub3-b3g1-next__hint">' +
              esc(fill(S.nextTierHint, { n: q.nextTier.addShirts, amount: M(q.nextTier.eachCents) })) +
              '</span>';
          }
          elNext.setAttribute('data-cheap', cheap ? 'true' : 'false');
          elNext.innerHTML =
            '<strong class="ub3-b3g1-next__line">' + esc(line) + '</strong>' +
            (unlock ? '<span class="ub3-b3g1-next__unlock">' + esc(unlock) + '</span>' : '') +
            hint;
        }
      }

      if (elMeter) {
        if (elMeterF) {
          elMeterF.style.setProperty('--ub3-meter-pct',
            Math.min(100, Math.round((q.filled / ULTRA_AT) * 100)) + '%');
        }
        elMeter.classList.toggle('ub3-meter--complete', q.ultra);
      }

      /* ── the money ───────────────────────────────────────────────────── */
      if (elLedger) {
        var rows = '';
        for (i = 0; i < MAXSLOTS; i++) {
          var p = slots[i];
          if (!p) continue;
          var off = (p.price !== UNIT);
          rows += '<div class="ub3-ledger__row">' +
            '<b class="ub3-truncate">' + esc(shortTitle(p.title)) +
              ' <span class="ub3-muted ub3-num">' + esc(p.size || '') + '</span></b>' +
            '<span class="ub3-num' + (off ? ' ub3-warn' : '') + '">' + esc(M(p.price)) + '</span>' +
            '</div>';
        }
        rows += '<div class="ub3-ledger__total">' +
          '<span class="ub3-eyebrow">' + esc(S.total) +
            (q.filled ? ' <span class="ub3-num">' + q.filled + '</span>' : '') + '</span>' +
          '<span class="ub3-b3g1-tot">' +
            (q.saveCents > 0 ? '<span class="ub3-strike">' + esc(M(q.filled * UNIT)) + '</span> ' : '') +
            '<b class="ub3-price ub3-price--lg">' + esc(M(q.totalCents)) + '</b>' +
          '</span>' +
          '</div>';
        if (q.filled > 0) {
          rows += '<p class="ub3-meta' + (q.saveCents > 0 ? ' ub3-free' : ' ub3-muted') + '">' +
            esc(fill(S.tierNow, { n: q.filled, amount: M(q.eachCents) })) +
            (q.saveCents > 0 ? ' · ' + esc(fill(S.save, { amount: M(q.saveCents) })) : '') +
            '</p>';
        }
        elLedger.innerHTML = rows;
      }

      /* ── ULTRA perks. Data-driven: the list comes from the snippet's config,
         which COMPOSITION populates, so perks are added or removed without a
         code change here. `soon` perks are rendered as pending, never as
         unlocked — the owner is still deciding on several. ──────────────── */
      if (elPerks) {
        if (!PERKS.length) {
          elPerks.hidden = true;
        } else {
          elPerks.hidden = false;
          elPerks.setAttribute('data-unlocked', q.ultra ? 'true' : 'false');
          var items = '';
          for (i = 0; i < PERKS.length; i++) {
            var pk = PERKS[i];
            items += '<li class="ub3-b3g1-perk" data-state="' + esc(pk.state || 'live') + '">' +
              '<span class="ub3-b3g1-perk__tick" aria-hidden="true">' + (q.ultra ? '✓' : '·') + '</span>' +
              '<span>' + esc(pk.label) + '</span>' +
              (pk.state === 'soon' ? '<em class="ub3-meta ub3-muted">' + esc(S.perkSoon) + '</em>' : '') +
              '</li>';
          }
          elPerks.innerHTML =
            '<p class="ub3-eyebrow' + (q.ultra ? ' ub3-free' : ' ub3-muted') + '">' +
              esc(q.ultra ? S.ultraOn : S.ultraPerks) + '</p>' +
            '<ul class="ub3-b3g1-perks">' + items + '</ul>';
        }
      }

      /* ── THE GUARD: the catalogue is not repriced yet ─────────────────── */
      if (elNote) {
        if (q.flatMismatch) {
          elNote.hidden = false;
          elNote.innerHTML =
            '<b>' + esc(q.offCount === 1
              ? fill(S.mismatchOne, { amount: M(UNIT) })
              : fill(S.mismatchMany, { n: q.offCount, amount: M(UNIT) })) + '</b> ' +
            esc(fill(S.mismatchWhat, { amount: M(q.listTotalCents) }));
        } else if (cartKnown && cartTees.length) {
          elNote.hidden = false;
          elNote.textContent = S.cartNote;
        } else {
          elNote.hidden = true;
          elNote.textContent = '';
        }
      }

      /* ── free shipping: a COUNT gate at 3 ─────────────────────────────── */
      if (elShip) {
        elShip.hidden = false;
        if (elShipFill) {
          elShipFill.style.setProperty('--ub3-meter-pct',
            Math.min(100, Math.round((q.filled / FREE_SHIP_AT) * 100)) + '%');
        }
        if (elShipTxt) {
          elShipTxt.textContent = q.freeShip
            ? S.shipOpen
            : (q.shirtsToFreeShip === 1 ? S.shipLocked1
                                        : fill(S.shipLocked, { n: q.shirtsToFreeShip }));
        }
        var mw = elShip.querySelector('.ub3-meter');
        if (mw) mw.classList.toggle('ub3-meter--complete', !!q.freeShip);
      }

      if (elCtaTxt) {
        elCtaTxt.textContent = !q.filled
          ? S.ctaEmpty
          : (q.filled === 1 ? fill(S.ctaAdd1, { amount: M(q.totalCents) })
                            : fill(S.ctaAdd, { n: q.filled, amount: M(q.totalCents) }));
      }
      if (elCta) {
        elCta.disabled = !q.filled;
        elCta.classList.toggle('ub3-btn--free', q.ultra);
        elCta.classList.toggle('ub3-btn--primary', !q.ultra);
      }

      renderStrip();
      emit('ub3:change', { chosen: slots.slice(0), quote: q, filled: q.filled });
      return q;
    }

    function renderAll() { renderSizes(); renderSum(); renderSlots(); }

    /* ======================================================================
       THE PICKER SHEET
       ====================================================================== */
    var sheet = null, elGrid, elQ, elMore, elEmpty, elSlotN, elStrip, elFoot, elSheetTotal, elSheetSizes, elScroll;
    var activeSlot = 0, sortKey = 'trend', shown = PAGE_CARDS, lastFocus = null;
    var qTimer = null, renderedKey = '';

    /* The picker's guts, shared by both layouts. In `page` layout this markup
       is injected straight into the module and is on screen the whole time —
       the grid IS the page, and adding a shirt is one tap with nothing to open.
       In `column` layout the same markup goes into a fixed sheet. */
    function pickerInnerHTML() {
      return '<p class="ub3-meta ub3-muted ub3-b3g1-gridsub">' + esc(S.gridSub) + '</p>' +
        '<div class="ub3-b3g1-tools">' +
          '<div class="ub3-search">' +
            '<input type="search" class="ub3-search__input" data-ub3-q placeholder="' + esc(S.searchPh) +
              '" autocomplete="off" spellcheck="false" aria-label="' + esc(S.searchPh) + '">' +
          '</div>' +
          '<div class="ub3-chipset ub3-chipset--rail" role="tablist" aria-label="Sort">' +
            '<button type="button" class="ub3-chip ub3-chip--filter" data-ub3-sort="trend" role="tab" aria-pressed="true" aria-selected="true">' + esc(S.tabTrend) + '</button>' +
            '<button type="button" class="ub3-chip ub3-chip--filter" data-ub3-sort="new" role="tab" aria-pressed="false" aria-selected="false">' + esc(S.tabNew) + '</button>' +
            '<button type="button" class="ub3-chip ub3-chip--filter" data-ub3-sort="all" role="tab" aria-pressed="false" aria-selected="false">' + esc(S.tabAll) + '</button>' +
          '</div>' +
          '<div class="ub3-chipset ub3-chipset--rail ub3-b3g1-szrail" data-ub3-sheetsizes role="group" aria-label="' + esc(S.yourSize) + '"></div>' +
        '</div>' +
        '<div class="ub3-b3g1-scroll" data-ub3-scroll>' +
          '<div class="ub3-grid ub3-grid--auto ub3-b3g1-grid" data-ub3-grid></div>' +
          '<div class="ub3-empty" data-ub3-empty hidden><p>' + esc(S.empty) + '</p></div>' +
          '<button type="button" class="ub3-btn ub3-btn--secondary ub3-btn--block ub3-b3g1-more" data-ub3-more hidden>' + esc(S.loadMore) + '</button>' +
        '</div>';
    }

    function wire(host) {
      elGrid   = host.querySelector('[data-ub3-grid]');
      elQ      = host.querySelector('[data-ub3-q]');
      elMore   = host.querySelector('[data-ub3-more]');
      elEmpty  = host.querySelector('[data-ub3-empty]');
      elSheetSizes = host.querySelector('[data-ub3-sheetsizes]');
      elScroll = host.querySelector('[data-ub3-scroll]');
      host.addEventListener('click', onSheetClick);
      elQ.addEventListener('input', function () {
        clearTimeout(qTimer);
        var v = elQ.value.trim();
        shown = PAGE_CARDS;
        paint();                                   /* paint local hits FIRST */
        qTimer = setTimeout(function () {
          if (v.length >= 2) {
            store.suggest(v).then(function (n) { if (n) paint(); });
            store.more().then(function (n) { if (n) paint(); });
          }
        }, 180);
      });
      var scroller = (LAYOUT === 'page') ? W : elScroll;
      scroller.addEventListener('scroll', function () {
        var nearEnd;
        if (LAYOUT === 'page') {
          var r = elScroll.getBoundingClientRect();
          nearEnd = r.bottom < (W.innerHeight || 800) + 900;
        } else {
          nearEnd = elScroll.scrollTop + elScroll.clientHeight > elScroll.scrollHeight - 600;
        }
        if (nearEnd) grow();
      }, { passive: true });
    }

    function buildSheet() {
      if (sheet) return sheet;

      /* ── PAGE LAYOUT (the new default) ────────────────────────────────────
         There is nothing to open. The grid is part of the page, permanently on
         screen, and adding a shirt is a single tap. */
      if (LAYOUT === 'page') {
        var inline = rootEl.querySelector('[data-ub3-picker]');
        if (!inline) return null;
        inline.innerHTML = pickerInnerHTML();
        sheet = inline;
        sheet.__inline = true;
        wire(inline);
        return sheet;
      }

      /* ── COLUMN LAYOUT ───────────────────────────────────────────────────
         ⚠ HOST ON <body>, NOT INSIDE THE SECTION. theme.css:517 sets
         `details, header, main { overflow: hidden }` and the existing pack
         picker was clipped by it — the overlay hit-tested but never painted.
         DESIGN wants sheets inside the `.ub3` scope so they inherit ground, ink
         and the focus ring; both are satisfied by hosting a `.ub3` wrapper on
         <body> and putting the sheet inside that. */
      var host = D.createElement('div');
      host.className = 'ub3 ub3-b3g1-host';
      host.innerHTML =
        '<div class="ub3-scrim" data-ub3-scrim hidden></div>' +
        '<div class="ub3-sheet ub3-b3g1-sheet" data-ub3-sheet role="dialog" aria-modal="true" aria-labelledby="ub3-sheet-t" hidden>' +
          '<div class="ub3-sheet__grab" aria-hidden="true"></div>' +
          '<div class="ub3-sheet__head">' +
            '<div>' +
              '<p class="ub3-eyebrow" id="ub3-sheet-t" data-ub3-slotn></p>' +
              '<p class="ub3-meta ub3-muted" data-ub3-sheetsub></p>' +
            '</div>' +
            '<button type="button" class="ub3-sheet__close" data-ub3-close aria-label="Close">&times;</button>' +
          '</div>' +
          pickerInnerHTML() +
          '<div class="ub3-sheet__foot">' +
            '<div class="ub3-b3g1-strip" data-ub3-strip></div>' +
            '<button type="button" class="ub3-btn ub3-btn--primary ub3-b3g1-donebtn" data-ub3-close>' + esc(S.done) + '</button>' +
          '</div>' +
        '</div>';
      D.body.appendChild(host);
      sheet = host.querySelector('[data-ub3-sheet]');
      sheet.__host = host;
      sheet.__scrim = host.querySelector('[data-ub3-scrim]');
      elSlotN = sheet.querySelector('[data-ub3-slotn]');
      elStrip = sheet.querySelector('[data-ub3-strip]');
      elFoot = sheet.querySelector('.ub3-sheet__foot');
      elSheetTotal = sheet.querySelector('[data-ub3-sheetsub]');
      wire(host);
      return sheet;
    }

    function grow() {
      var list = filtered();
      if (shown < list.length) { shown += PAGE_CARDS; paint(); return; }
      if (!store.collDone && !elQ.value.trim()) {
        store.more().then(function (n) { if (n) { shown += PAGE_CARDS; paint(); } });
      }
    }

    function filtered() {
      var q = (elQ && elQ.value || '').trim().toLowerCase();
      var rows = store.all(), out = [], i;
      for (i = 0; i < rows.length; i++) {
        var p = rows[i];
        if (!p.hasStock) continue;
        if (!isTeeTitle(p.t)) continue;                       /* also drops Oversized */
        if (cfg.exclude && cfg.exclude.length && cfg.exclude.indexOf(p.h) > -1) continue;
        /* strict_flat: after the reprice, a shirt that is not $35 simply is not
           in the offer and must not be offerable. Before the reprice this is
           off, and the mismatch is surfaced in the ledger instead. */
        if (STRICT && p.min !== UNIT) continue;
        if (q && p.t.toLowerCase().indexOf(q) === -1) continue;
        out.push(p);
      }
      var pinId = cur.id;
      out.sort(function (a, b) {
        var pin = (b.id === pinId ? 1 : 0) - (a.id === pinId ? 1 : 0);
        if (pin) return pin;
        if (sortKey === 'new') return (b.pub || 0) - (a.pub || 0) || a._r - b._r;
        if (sortKey === 'all') return (a._c == null ? 1e9 : a._c) - (b._c == null ? 1e9 : b._c) || a._r - b._r;
        return a._r - b._r;                       /* merchandised / best-selling order */
      });
      return out;
    }

    function cardHTML(p) {
      var v = variantOf(p, prefSize);
      var inSize = !!(v && v.a);
      var picked = false, i;
      for (i = 0; i < MAXSLOTS; i++) if (slots[i] && slots[i].pid === p.id) picked = true;
      var isNew = p.pub && (Date.now() - p.pub) < 22 * 864e5;
      /* Cards are pure ARTWORK — these are graphic tees and people choose with
         their eyes. Title and price are secondary, the print is the product. */
      return '<div class="ub3-card ub3-card--flat ub3-card--interactive ub3-b3g1-card"' +
        ' data-ub3-card="' + p.id + '" data-selected="' + (picked ? 'true' : 'false') + '">' +
        '<button type="button" class="ub3-b3g1-card__hit" data-ub3-pick="' + p.id + '">' +
          '<span class="ub3-card__media">' +
            (p.img ? '<img src="' + esc(p.img) + '" alt="' + esc(shortTitle(p.t)) + '" loading="lazy" decoding="async">' : '') +
            (isNew ? '<span class="ub3-badge ub3-badge--accent ub3-card__flag">' + esc(S.badgeNew) + '</span>' : '') +
            (p.id === cur.id ? '<span class="ub3-badge ub3-badge--outline ub3-b3g1-pin">' + esc(S.slotThisOne) + '</span>' : '') +
            (picked ? '<span class="ub3-badge ub3-badge--free ub3-b3g1-tick" aria-hidden="true">✓</span>' : '') +
          '</span>' +
          '<span class="ub3-card__body ub3-b3g1-card__body">' +
            '<span class="ub3-card__title ub3-truncate">' + esc(shortTitle(p.t)) + '</span>' +
            '<span class="ub3-card__price ub3-num">' + esc(M(p.min)) + '</span>' +
            (inSize ? '' : '<span class="ub3-card__meta ub3-warn">' +
              esc(fill(S.noSize, { size: prefSize || '' })) + '</span>') +
          '</span>' +
        '</button>' +
        /* the size escape hatch: always present, so a shopper can take one
           shirt in a different size without changing their bundle default */
        '<button type="button" class="ub3-b3g1-card__sz" data-ub3-sizes="' + p.id + '"' +
          ' aria-label="Choose a size for ' + esc(shortTitle(p.t)) + '"' +
          ' aria-expanded="false">' + esc(inSize ? (prefSize || '·') : '···') + '</button>' +
        '<div class="ub3-chipset ub3-b3g1-szrow" data-ub3-szrow hidden>' + szRowHTML(p) + '</div>' +
      '</div>';
    }

    /* Sold-out sizes are SHOWN, struck through, never hidden. "This shirt
       exists in XL, just not right now" is merchandising; a missing chip just
       looks like the shirt does not come in your size. */
    function szRowHTML(p) {
      var list = sizesOf(p), html = '', i;
      for (i = 0; i < list.length; i++) {
        var sz = list[i];
        html += '<button type="button" class="ub3-chip ub3-chip--sm ub3-chip--size"' +
          ' data-ub3-picksize="' + p.id + '" data-ub3-sv="' + esc(sz.s) + '"' +
          (sz.a ? '' : ' data-oos disabled aria-disabled="true"') + '>' + esc(sz.s) + '</button>';
      }
      return html;
    }

    /* Incremental repaint: rebuilding the grid on every keystroke snapped open
       size rows shut and made the scroll position jump. Rebuild only when the
       visible prefix actually changed. (Idea lifted from umai-pack.js:823.) */
    var renderedIds = [];
    function paint() {
      if (!elGrid) return;
      var list = filtered();
      var slice = list.slice(0, shown);
      var key = sortKey + '|' + (elQ && elQ.value || '').trim().toLowerCase() + '|' + (prefSize || '');
      var moved = key !== renderedKey || slice.length !== renderedIds.length;
      if (!moved) {
        for (var k = 0; k < slice.length; k++) {
          if (renderedIds[k] !== slice[k].id) { moved = true; break; }
        }
      }
      if (moved) {
        var html = '';
        for (var i = 0; i < slice.length; i++) html += cardHTML(slice[i]);
        elGrid.innerHTML = html;
        renderedIds = slice.map(function (p) { return p.id; });
        renderedKey = key;
      }
      if (elEmpty) elEmpty.hidden = slice.length > 0;
      if (elMore) elMore.hidden = !(slice.length < list.length || (!store.collDone && !(elQ && elQ.value.trim())));
      renderStrip();
    }

    function renderStrip() {
      if (!elStrip) return;
      var q = lastQuote || quote();
      var html = '', i, cnt = slotCount(q);
      for (i = 0; i < cnt; i++) {
        var p = slots[i];
        var state = p ? (q.ultra ? 'locked' : 'filled')
                      : ((i === nextGap()) ? 'free' : 'empty');
        html += '<button type="button" class="ub3-slot ub3-b3g1-chip" data-state="' + state + '"' +
          (i === activeSlot ? ' data-active="true"' : '') + ' data-ub3-open="' + i + '"' +
          ' aria-label="Slot ' + (i + 1) + '">' +
          (p && p.img ? '<img class="ub3-slot__img" src="' + esc(p.img) + '" alt="">'
                      : '<span class="ub3-slot__n">' + (i + 1) + '</span>') +
          '</button>';
      }
      elStrip.innerHTML = html;
      if (elSheetTotal) {
        elSheetTotal.innerHTML = q.filled
          ? (num(M(q.totalCents)) + (q.qualifies && q.saveCents
              ? ' <span class="ub3-free">' + esc(fill(S.save, { amount: M(q.saveCents) })) + '</span>'
              : ''))
          : '';
      }
      if (elSlotN) elSlotN.textContent = S.gridTitle;
    }

    var lockY = 0;
    function lockPage(on) {
      var b = D.body;
      if (on) {
        lockY = W.pageYOffset || D.documentElement.scrollTop || 0;
        b.style.top = (-lockY) + 'px';
        b.style.position = 'fixed';
        b.style.width = '100%';
        b.classList.add('ub3-lock');
      } else {
        b.classList.remove('ub3-lock');
        b.style.position = ''; b.style.top = ''; b.style.width = '';
        /* iOS loses the PDP scroll position without this — the old pack picker
           set body{overflow:hidden} with no restore and dumped you at the top. */
        try { W.scrollTo(0, lockY); } catch (e) {}
      }
    }

    /* A real focus trap. `aria-modal` alone does not stop Tab escaping into the
       page behind — the pack picker set the attribute and never backed it. */
    function trapTab(e) {
      if (e.key !== 'Tab' || !sheet || sheet.hidden) return;
      var f = sheet.querySelectorAll('button:not([disabled]),input,[href],select,[tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && D.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function openSheet(i) {
      buildSheet();
      if (LAYOUT === 'page') {
        /* nothing to open — aim the next pick at this slot and bring the grid
           to the shopper */
        activeSlot = (typeof i === 'number' && i >= 0 && i < MAXSLOTS) ? i : nextGap();
        if (activeSlot < 0) activeSlot = MAXSLOTS - 1;
        renderSlots();
        var g = rootEl.querySelector('[data-ub3-picker]');
        try { if (g && g.scrollIntoView) g.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
        emit('ub3:open', { slot: activeSlot });
        return;
      }
      activeSlot = (typeof i === 'number' && i >= 0 && i < MAXSLOTS) ? i : nextGap();
      if (activeSlot < 0) activeSlot = MAXSLOTS - 1;
      lastFocus = D.activeElement;
      shown = PAGE_CARDS;
      renderedKey = '';
      sheet.hidden = false;
      sheet.__scrim.hidden = false;
      lockPage(true);
      /* two frames: the element must be laid out unhidden before the transform
         transition can run, or the sheet snaps in with no motion */
      W.requestAnimationFrame(function () {
        W.requestAnimationFrame(function () {
          sheet.setAttribute('data-open', 'true');
          sheet.__scrim.setAttribute('data-open', 'true');
        });
      });
      renderSizes();
      paint();
      renderStrip();
      bootstrap();
      D.addEventListener('keydown', trapTab);
      setTimeout(function () { try { elQ.focus({ preventScroll: true }); } catch (e) {} }, 60);
      track('open', { slot: activeSlot, of: MAXSLOTS, filled: filledCount() });
      emit('ub3:open', { slot: activeSlot });
    }

    function closeSheet() {
      if (LAYOUT === 'page') return;
      if (!sheet || sheet.hidden) return;
      sheet.removeAttribute('data-open');
      sheet.__scrim.removeAttribute('data-open');
      D.removeEventListener('keydown', trapTab);
      lockPage(false);
      var s2 = sheet;
      setTimeout(function () {
        if (!s2.hasAttribute('data-open')) { s2.hidden = true; s2.__scrim.hidden = true; }
      }, 340);
      try { if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true }); } catch (e) {}
      track('close', { filled: filledCount(), of: MAXSLOTS });
      emit('ub3:close', { slot: activeSlot });
    }

    var bootstrapped = false;
    function bootstrap() {
      if (bootstrapped) return;
      bootstrapped = true;
      /* One page is enough to fill several screens; the rest arrives on scroll
         or on a search that local rows cannot answer. Deliberately not eager —
         products.json is a fat endpoint and this is a phone. */
      store.more().then(function (n) { if (n) paint(); }).catch(function () {});
    }

    function nextGap() {
      for (var i = 0; i < MAXSLOTS; i++) if (!slots[i]) return i;
      return -1;
    }
    function filledCount() {
      var n = 0;
      for (var i = 0; i < MAXSLOTS; i++) if (slots[i]) n++;
      return n;
    }

    function pick(pid, sizeName) {
      var row = store.get(pid);
      if (!row) return;
      var v = variantOf(row, sizeName || prefSize);
      if (!v || !v.a) {
        /* no size chosen yet, or the default is not stocked — open the row */
        toggleSizes(pid, true);
        return;
      }
      if (!prefSize) setSize(v.s, true);
      slots[activeSlot] = makePick(row, v);
      var next = nextGap();
      renderSlots(); renderSum(); save();
      track('add_shirt', { product: row.h, size: v.s, slot: activeSlot, of: MAXSLOTS, price: v.p / 100 });
      if (next === -1) {
        activeSlot = MAXSLOTS - 1;
        renderStrip();
        paint();
        if (elFoot) elFoot.setAttribute('data-complete', 'true');
        track('complete', { of: MAXSLOTS });
      } else {
        activeSlot = next;
        renderStrip();
        paint();
      }
    }

    function toggleSizes(pid, force) {
      if (!elGrid) return;
      var card = elGrid.querySelector('[data-ub3-card="' + pid + '"]');
      if (!card) return;
      var row = card.querySelector('[data-ub3-szrow]');
      var tog = card.querySelector('[data-ub3-sizes]');
      if (!row) return;
      var open = force === true ? true : row.hidden;
      /* accordion of one — two open size rows on a 375px screen is a mess */
      var rows = elGrid.querySelectorAll('[data-ub3-szrow]');
      for (var i = 0; i < rows.length; i++) rows[i].hidden = true;
      var togs = elGrid.querySelectorAll('[data-ub3-sizes]');
      for (i = 0; i < togs.length; i++) togs[i].setAttribute('aria-expanded', 'false');
      var cards = elGrid.querySelectorAll('[data-open-sizes]');
      for (i = 0; i < cards.length; i++) cards[i].removeAttribute('data-open-sizes');
      row.hidden = !open;
      if (tog) tog.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) card.setAttribute('data-open-sizes', 'true');
      if (open && row.scrollIntoView) {
        try { row.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
      }
    }

    function onSheetClick(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-ub3-scrim]') || t.closest('[data-ub3-close]')) { closeSheet(); return; }

      var sortBtn = t.closest('[data-ub3-sort]');
      if (sortBtn) {
        sortKey = sortBtn.getAttribute('data-ub3-sort');
        var tabs = sheet.querySelectorAll('[data-ub3-sort]');
        for (var i = 0; i < tabs.length; i++) {
          var on = tabs[i] === sortBtn;
          tabs[i].setAttribute('aria-pressed', on ? 'true' : 'false');
          tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        }
        if (elQ) elQ.value = '';
        shown = PAGE_CARDS;
        if (elScroll) elScroll.scrollTop = 0;
        paint();
        track('sort', { tab: sortKey });
        return;
      }
      var chip = t.closest('[data-ub3-open]');
      if (chip) {
        activeSlot = parseInt(chip.getAttribute('data-ub3-open'), 10) || 0;
        renderStrip();
        return;
      }
      var szBtn = t.closest('[data-ub3-picksize]');
      if (szBtn) {
        if (szBtn.disabled) return;
        pick(parseInt(szBtn.getAttribute('data-ub3-picksize'), 10), szBtn.getAttribute('data-ub3-sv'));
        return;
      }
      var moreSz = t.closest('[data-ub3-sizes]');
      if (moreSz) { toggleSizes(parseInt(moreSz.getAttribute('data-ub3-sizes'), 10)); return; }
      var hit = t.closest('[data-ub3-pick]');
      if (hit) { pick(parseInt(hit.getAttribute('data-ub3-pick'), 10)); return; }
      var more = t.closest('[data-ub3-more]');
      if (more) { grow(); return; }
    }

    /* ======================================================================
       MODULE-LEVEL EVENTS
       ====================================================================== */
    rootEl.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var setSz = t.closest('[data-ub3-setsize]');
      if (setSz) { setSize(setSz.getAttribute('data-ub3-setsize')); track('size', { size: prefSize }); return; }
      var clear = t.closest('[data-ub3-clear]');
      if (clear) {
        var ci = parseInt(clear.getAttribute('data-ub3-clear'), 10);
        slots[ci] = null;
        activeSlot = ci;                 /* retarget, or the next pick overwrites a full slot */
        renderSlots(); renderSum(); save();
        return;
      }
      var slotSz = t.closest('[data-ub3-slotsize]');
      if (slotSz) {
        var i2 = parseInt(slotSz.getAttribute('data-ub3-slotsize'), 10);
        openSheet(i2);
        if (slots[i2]) setTimeout(function () { toggleSizes(slots[i2].pid, true); }, 40);
        return;
      }
      var open = t.closest('[data-ub3-open]');
      if (open) { openSheet(parseInt(open.getAttribute('data-ub3-open'), 10)); return; }
      if (t.closest('[data-ub3-cta]')) { addBundle(); return; }
    });

    if (D) {
      D.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && LAYOUT !== 'page' && sheet && !sheet.hidden) closeSheet();
      });
    }

    /* ======================================================================
       ADD TO BAG — one POST, all four lines
       ====================================================================== */
    var adding = false;
    function addBundle() {
      if (elErr) elErr.textContent = '';
      /* The ladder has no minimum — one shirt is a legitimate purchase and the
         module must never block it. Only an EMPTY set is refused, and then by
         pointing at the first slot rather than by an error. */
      if (filledCount() === 0) {
        var el = elSlots && elSlots.querySelector('[data-ub3-slot="0"]');
        if (el) { el.classList.remove('ub3-flash'); void el.offsetWidth; el.classList.add('ub3-flash'); }
        openSheet(0);
        return;
      }
      if (adding) return;
      adding = true;

      var q = lastQuote || quote();
      var merged = {}, order = [], i;
      for (i = 0; i < MAXSLOTS; i++) {
        var p = slots[i];
        if (!p) continue;
        if (merged[p.vid]) { merged[p.vid].quantity++; continue; }
        merged[p.vid] = { id: p.vid, quantity: 1 };
        order.push(merged[p.vid]);
      }

      var label = elCtaTxt ? elCtaTxt.textContent : '';
      if (elCta) elCta.disabled = true;
      if (elCtaTxt) elCtaTxt.textContent = S.ctaAdding;

      var base = (cfg.routes && cfg.routes.cartAdd) || '/cart/add';
      fetch(base + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: order })
      }).then(function (r) {
        if (!r.ok) {
          return r.text().then(function (tx) {
            var m = '';
            try { var j = JSON.parse(tx); m = (j && typeof j.description === 'string') ? j.description : ''; } catch (e) {}
            throw new Error(m || S.addErr);
          }, function () { throw new Error(S.addErr); });
        }
        /* ⚠ EVERYTHING PAST THIS POINT RUNS AFTER THE CART HAS ALREADY CHANGED.
           A throw here shows an error on an add that worked, leaves the slots
           full, and the next tap double-charges. That exact bug shipped on
           2026-08-07 (umai-pack.js: an undeclared `cartTees` in the success
           handler). So: the whole tail is wrapped, and nothing in it may be
           allowed to reject. */
        try {
          track('add_to_cart', {
            of: MAXSLOTS, value: q.totalCents / 100, save: q.saveCents / 100, mode: q.mode
          });
          for (var z = 0; z < MAXSLOTS; z++) slots[z] = null;
          ss(BUNDLE_KEY, JSON.stringify({ t: 0, s: [], z: prefSize }));
          slots[0] = defaultSlot0();
          renderSlots(); renderSum();
          if (elCtaTxt) elCtaTxt.textContent = S.ctaAdded;
          closeSheet();
          emit('cart:refresh', {});
          emit('ub3:added', { items: order, quote: q });
        } catch (e2) { /* never let a repaint bug look like a failed add */ }
        setTimeout(function () {
          if (elCta) elCta.disabled = false;
          adding = false;
          renderSum();
        }, 1400);
      }).catch(function (err) {
        adding = false;
        if (elCta) elCta.disabled = false;
        if (elCtaTxt) elCtaTxt.textContent = label;
        var msg = (err && err.message && err.message.length < 200) ? err.message : S.addErr;
        if (elErr) elErr.textContent = msg;
        emit('ub3:error', { message: msg });
        track('add_error', { message: msg });
      });
    }

    /* ======================================================================
       BOOT
       ====================================================================== */
    function defaultSlot0() {
      var v = null, i;
      for (i = 0; i < cur.v.length; i++) if (cur.v[i].id === cfg.current.vid) v = cur.v[i];
      if ((!v || !v.a) && prefSize) v = variantOf(cur, prefSize);
      if (!v || !v.a) v = firstAvailable(cur);
      return v ? makePick(cur, v) : null;
    }

    prefSize = ls(SIZE_KEY) || null;
    restore();
    slots[0] = defaultSlot0();
    if (slots[0] && !prefSize) prefSize = slots[0].size;
    if (prefSize) setSize(prefSize, true);

    renderAll();
    rootEl.hidden = false;
    readCart();

    var cartT = null;
    ['cart:updated', 'cart:refresh'].forEach(function (ev) {
      D.addEventListener(ev, function () { clearTimeout(cartT); cartT = setTimeout(readCart, 250); });
    });
    /* COMPOSITION can keep slot 1 in step with the PDP's own size chips. */
    D.addEventListener('ub3:slot1', function (e) {
      var vid = e && e.detail && e.detail.variantId;
      if (!vid) return;
      for (var i = 0; i < cur.v.length; i++) {
        if (cur.v[i].id === vid) {
          slots[0] = makePick(cur, cur.v[i]);
          if (cur.v[i].a) setSize(cur.v[i].s, true);
          renderAll(); save();
          return;
        }
      }
    });

    W.UMAI_B3G1 = {
      open: openSheet,
      close: closeSheet,
      setSize: function (s) { setSize(s); },
      setSlot: function (i, vid) {
        var row = null, v = null, rows = store.all(), a, b;
        for (a = 0; a < rows.length; a++) for (b = 0; b < rows[a].v.length; b++) {
          if (rows[a].v[b].id === vid) { row = rows[a]; v = rows[a].v[b]; }
        }
        if (row && v) { slots[i] = makePick(row, v); renderSlots(); renderSum(); save(); }
      },
      quote: quote,
      price: priceBundle,
      slots: function () { return slots.slice(0); }
    };

    track('view', { seeded: store.all().length });
    emit('ub3:ready', { root: rootEl, chosen: slots.slice(0), quote: lastQuote });
  }

  /* ==========================================================================
     8. UTILITIES
     ========================================================================== */
  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function shortTitle(t) {
    /* "Angel Cop • T-Shirt" -> "Angel Cop". Four of these have to fit on a
       375px row; the word T-Shirt is the one word that carries no information
       inside a t-shirt bundle. */
    return String(t || '')
      .replace(/\s*[•|\-–—]\s*(Oversized\s+)?T-?Shirt\s*$/i, '')
      .replace(/\s*T-?Shirt\s*$/i, '')
      .trim() || String(t || '');
  }

  function boot() {
    if (!D || !D.querySelectorAll) return;
    var list = D.querySelectorAll('[data-ub3-bundle]');
    for (var i = 0; i < list.length; i++) {
      try { init(list[i]); } catch (e) {
        /* A picker that fails to boot must leave the page's normal
           single-shirt purchase completely untouched. Stay hidden, say nothing. */
        try { if (W.console && console.warn) console.warn('[ub3]', e); } catch (e2) {}
      }
    }
  }
  if (D) {
    if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot);
    else boot();
    D.addEventListener('shopify:section:load', boot);
  }
})();
